import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {mockBusiness} from '../../mock-data';

const MIN_PURCHASE_AMOUNT = 250;

const PurchaseBizDialog = ({open, onClose, onCreated}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.buy'});
    const isMounted = useIsMounted();
    const [submitting, setSubmitting] = useState(false);
    const [eftResult, setEftResult] = useState(null);
    const [formKey, setFormKey] = useState(0);

    /**
     * Resets dialog state whenever it opens.
     */
    useEffect(() => {
        if (open) {
            setEftResult(null);
            setSubmitting(false);
            setFormKey((k) => k + 1);
        }
    }, [open]);

    const schema = useMemo(() => Yup.object().shape({
        amount: Yup.number()
            .typeError(t('amountRequired'))
            .required(t('amountRequired'))
            .min(MIN_PURCHASE_AMOUNT, t('minAmount', {min: MIN_PURCHASE_AMOUNT})),
    }), [t]);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);
    const initialValues = useMemo(() => ({amount: ''}), []);

    /**
     * Creates a mock purchase request and surfaces EFT instructions.
     */
    const handleSubmit = useCallback(async (values) => {
        const parsedAmount = Number(values.amount);
        const previewBiz = Math.floor((parsedAmount / 100) * mockBusiness.rateBizPer100);
        setSubmitting(true);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({
            resultFlag: true,
            data: {
                ref: `BP-2026-${String(Math.floor(Math.random() * 9000) + 1000)}`,
                eftRef: `EFT-MH-${Date.now().toString().slice(-4)}`,
            },
        });
        if (!isMounted()) {
            return;
        }
        setSubmitting(false);
        if (response?.resultFlag) {
            const newPurchase = {
                ref: response.data.ref,
                date: new Date().toISOString().slice(0, 10),
                amt: parsedAmount,
                biz: previewBiz,
                status: 'Awaiting transfer',
            };
            setEftResult(response.data);
            onCreated?.(newPurchase);
        }
    }, [isMounted, onCreated]);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose?.();
                }
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle component="div">
                <Typography variant="h6" sx={{p: 2}}>{t('purchaseDialogTitle')}</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        {t('purchaseDialogSubtitle', {
                            min: MIN_PURCHASE_AMOUNT,
                            rate: mockBusiness.rateBizPer100,
                            tier: mockBusiness.tier,
                        })}
                    </Typography>

                    {!eftResult && (
                        <Form
                            key={formKey}
                            initialValues={initialValues}
                            validate={validate}
                            onSubmit={handleSubmit}
                            formProps={{id: 'biz-point-purchase-biz-form', className: 'w-100'}}
                        >
                            {({values}) => {
                                const parsedAmount = Number(values.amount) || 0;
                                const previewBiz = Math.floor((parsedAmount / 100) * mockBusiness.rateBizPer100);
                                const redeemDollars = Math.round(previewBiz * mockBusiness.redeemPer900 / 900);
                                const isValid = parsedAmount >= MIN_PURCHASE_AMOUNT;
                                return (
                                    <Stack spacing={2}>
                                        <MuiRFF.TextField
                                            name="amount"
                                            label={t('amountLabel', {currency: mockBusiness.currency})}
                                            type="number"
                                            fullWidth
                                            variant="outlined"
                                            required={required.amount}
                                            helperText={t('minAmount', {min: MIN_PURCHASE_AMOUNT})}
                                            inputProps={{min: MIN_PURCHASE_AMOUNT}}
                                        />
                                        <Alert severity={isValid ? 'success' : 'warning'}>
                                            {isValid
                                                ? t('bizPreviewSuccess', {
                                                    biz: previewBiz.toLocaleString(),
                                                    dollars: redeemDollars.toLocaleString(),
                                                })
                                                : t('bizPreviewWarning', {min: MIN_PURCHASE_AMOUNT})}
                                        </Alert>
                                    </Stack>
                                );
                            }}
                        </Form>
                    )}

                    {eftResult && (
                        <Alert severity="info">
                            <Typography variant="subtitle2" sx={{mb: 0.5}}>{t('eftInstructions')}</Typography>
                            <Typography variant="body2">{t('paymentRef', {ref: eftResult.ref})}</Typography>
                            <Typography variant="body2">{t('eftReference', {eftRef: eftResult.eftRef})}</Typography>
                            <Typography variant="body2" sx={{mt: 1}}>
                                {t('eftBankHint')}
                            </Typography>
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <Divider/>
            <DialogActions sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="0 8px 0 8px"
                    onClick={onClose}
                >
                    {eftResult ? t('close') : t('cancel')}
                </Button>
                {!eftResult && (
                    <Box sx={{display: 'flex'}}>
                        <Button
                            variant="dialog"
                            color="primary"
                            borderRadius="8px 0 8px 0"
                            type="submit"
                            form="biz-point-purchase-biz-form"
                            disabled={submitting}
                        >
                            {t('createRequest')}
                        </Button>
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PurchaseBizDialog;
